import classes from './icons-library.module.scss';
import * as UiControls from '@nice-ccf/ui-controls';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { IMAGE_ASSET_FILENAMES, humanizeAssetName } from './image-assets';

type IconComponent = React.ComponentType<{
  fontSize?: 'small' | 'medium' | 'large' | 'inherit';
}>;

interface IconEntry {
  name: string;
  label: string;
  Component: IconComponent;
}

/**
 * Function to turn a PascalCase icon component name into a readable label
 * @param name - PascalCase icon component name, e.g. "CcfAcdConfigurationIcon"
 * @example humanizeIconName('CcfAcdConfigurationIcon')
 * @returns "Acd Configuration"
 */
const humanizeIconName = (name: string): string =>
  name
    .replace(/^Ccf/, '')
    .replace(/Icon$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();

const ICON_ENTRIES: IconEntry[] = Object.entries(UiControls)
  .filter(
    ([name, value]) =>
      name.startsWith('Ccf') && name.endsWith('Icon') && typeof value === 'function'
  )
  .map(([name, Component]) => ({
    name,
    label: humanizeIconName(name),
    Component: Component as IconComponent,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const IMAGE_ENTRIES = IMAGE_ASSET_FILENAMES.map((filename) => ({
  filename,
  label: humanizeAssetName(filename),
})).sort((a, b) => a.filename.localeCompare(b.filename));

type LibraryTab = 'icons' | 'images';

/* eslint-disable-next-line */
export interface IconsLibraryProps {}

/**
 * Browsable, searchable library of every SVG icon component and static image asset in the app;
 * click a card to copy its import statement (icons) or asset path (images)
 * @param props - IconsLibraryProps
 * @example <IconsLibrary />
 * @returns
 */
export function IconsLibrary(props: IconsLibraryProps) {
  const [tab, setTab] = useState<LibraryTab>('icons');
  const [search, setSearch] = useState('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const filteredIcons = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return ICON_ENTRIES;
    return ICON_ENTRIES.filter(
      ({ name, label }) =>
        name.toLowerCase().includes(query) || label.toLowerCase().includes(query)
    );
  }, [search]);

  const filteredImages = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return IMAGE_ENTRIES;
    return IMAGE_ENTRIES.filter(
      ({ filename, label }) =>
        filename.toLowerCase().includes(query) || label.toLowerCase().includes(query)
    );
  }, [search]);

  /**
   * Function to copy text to the clipboard and briefly flag which card was copied
   * @param text - the string to copy
   * @param key - the card's unique key, used to show a transient "Copied!" state
   * @example copyToClipboard("/assets/logo.png", "logo.png")
   */
  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1500);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <RouterLink to="/agent/home" className={classes.backLink}>
          ← Back to App
        </RouterLink>
        <h1 className={classes.title}>Icons &amp; Assets Library</h1>
        <p className={classes.subtitle}>
          {ICON_ENTRIES.length} SVG icons · {IMAGE_ENTRIES.length} image assets · click any card
          to copy
        </p>
      </div>

      <div className={classes.content}>
        <div className={classes.tabs}>
          <button
            type="button"
            className={tab === 'icons' ? classes.tabActive : classes.tab}
            onClick={() => setTab('icons')}
          >
            SVG Icons ({ICON_ENTRIES.length})
          </button>
          <button
            type="button"
            className={tab === 'images' ? classes.tabActive : classes.tab}
            onClick={() => setTab('images')}
          >
            Image Assets ({IMAGE_ENTRIES.length})
          </button>
        </div>

        <input
          className={classes.search}
          type="text"
          placeholder={tab === 'icons' ? 'Search icons by name...' : 'Search images by name...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {tab === 'icons' ? (
          <>
            <div className={classes.count}>Showing {filteredIcons.length} icons</div>
            <div className={classes.grid}>
              {filteredIcons.map(({ name, label, Component }) => (
                <button
                  key={name}
                  type="button"
                  className={classes.card}
                  onClick={() =>
                    copyToClipboard(`import { ${name} } from '@nice-ccf/ui-controls';`, name)
                  }
                >
                  <span className={classes.iconPreview}>
                    <Component fontSize="medium" />
                  </span>
                  <span className={classes.cardName}>{name}</span>
                  <span className={classes.cardLabel}>
                    {copiedKey === name ? 'Copied!' : label}
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className={classes.count}>Showing {filteredImages.length} images</div>
            <div className={classes.grid}>
              {filteredImages.map(({ filename, label }) => (
                <button
                  key={filename}
                  type="button"
                  className={classes.card}
                  onClick={() => copyToClipboard(`/assets/${filename}`, filename)}
                >
                  <span className={classes.imagePreview}>
                    <img src={`/assets/${encodeURIComponent(filename)}`} alt={label} />
                  </span>
                  <span className={classes.cardName}>{filename}</span>
                  <span className={classes.cardLabel}>
                    {copiedKey === filename ? 'Copied!' : label}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default IconsLibrary;
